import { useCallback, useEffect, useRef, useState } from 'react';
import { FormControl, FormHelperText, styled } from '@mui/material';
import { DateInputSegmented as DateInputSegmentedType } from '@/schemas/sceneDataSchema';
import { Controller, UseFormReturn } from 'react-hook-form';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { useFieldBinding, seedFormFromStore } from '@/hooks/use-field-binding';

interface SegmentValues {
  month: string;
  day: string;
  year: string;
}

type SegmentKey = keyof SegmentValues;

function parseValue(value: string | null): SegmentValues {
  if (!value) return { month: '', day: '', year: '' };
  const [year, month, day] = value.slice(0, 10).split('-');
  return { month: month ?? '', day: day ?? '', year: year ?? '' };
}

function compose({ month, day, year }: SegmentValues): string | null {
  if (/^\d{2}$/.test(month) && /^\d{2}$/.test(day) && /^\d{4}$/.test(year)) {
    return `${year}-${month}-${day}`;
  }
  return null;
}

function isObfuscated(segment: string): boolean {
  return segment.includes('*');
}

function formatForDisplay(value: string, segmentKey: SegmentKey, wasAutoPadded: boolean): string {
  if (isObfuscated(value)) return value.replace(/\*/g, '\u2022');
  if (segmentKey === 'year') return value;
  return wasAutoPadded ? value.replace(/^0/, '') : value;
}

const SEGMENT_CONFIG = [
  { key: 'month' as const, placeholder: 'MM', ariaLabel: 'Month', maxLength: 2 },
  { key: 'day' as const, placeholder: 'DD', ariaLabel: 'Day', maxLength: 2 },
  { key: 'year' as const, placeholder: 'YYYY', ariaLabel: 'Year', maxLength: 4 },
];

export const DateInputSegmentedFormElement = ({
  form,
  component,
}: {
  form: UseFormReturn;
  component: DateInputSegmentedType;
}) => {
  const key = encodeFieldKey(component);
  const binding = useFieldBinding(component);

  const [segments, setSegments] = useState<SegmentValues>(parseValue(component.value));
  const [cleared, setCleared] = useState({ month: false, day: false, year: false });
  const [autoPadded, setAutoPadded] = useState({ month: false, day: false, year: false });
  const [focusedSegment, setFocusedSegment] = useState<SegmentKey | null>(null);
  const [prevServerValue, setPrevServerValue] = useState(component.value);

  // Sync local segment state when server value changes (e.g., back-navigation).
  // useState only initializes on mount — this handles prop-driven updates.
  if (component.value !== prevServerValue) {
    setPrevServerValue(component.value);
    setSegments(parseValue(component.value));
    setCleared({ month: false, day: false, year: false });
    setAutoPadded({ month: false, day: false, year: false });
  }

  const wrapperRef = useRef<HTMLDivElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const refs = { month: monthRef, day: dayRef, year: yearRef };
  const isAutoAdvancing = useRef(false);

  useEffect(() => {
    seedFormFromStore(form, key, binding.value);
  }, [form, key, binding.value]);

  const syncIfComplete = useCallback(
    (next: SegmentValues, onChange: (v: string | null) => void) => {
      const composed = compose(next);
      if (composed) {
        onChange(composed);
        binding.sync(composed);
      }
    },
    [binding]
  );

  // Handles digit input for each segment with auto-padding and auto-advance.
  // When a segment is unambiguously complete (e.g. typing "2" for month can only mean "02"),
  // it zero-pads the value and advances focus to the next segment.
  const makeHandleChange = (onChange: (v: string | null) => void) => (segment: SegmentKey, raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, segment === 'year' ? 4 : 2);
    setAutoPadded((prev) => ({ ...prev, [segment]: false }));

    if (segment === 'month') {
      let next = { ...segments, month: digits };
      // Single digit >= 2 can only be 02–09; zero-pad and advance to day
      if (digits.length === 1 && Number(digits) >= 2) {
        next = { ...segments, month: `0${digits}` };
        setSegments(next);
        setAutoPadded((prev) => ({ ...prev, month: true }));
        syncIfComplete(next, onChange);
        isAutoAdvancing.current = true;
        dayRef.current?.focus();
        return;
      }
      // Two digits entered; reject invalid months (00, 13+) then advance
      if (digits.length === 2) {
        if (Number(digits) > 12 || Number(digits) === 0) return;
        setSegments(next);
        syncIfComplete(next, onChange);
        isAutoAdvancing.current = true;
        dayRef.current?.focus();
        return;
      }
      setSegments(next);
      syncIfComplete(next, onChange);
    } else if (segment === 'day') {
      let next = { ...segments, day: digits };
      // Single digit >= 4 can only be 04–09; zero-pad and advance to year
      if (digits.length === 1 && Number(digits) >= 4) {
        next = { ...segments, day: `0${digits}` };
        setSegments(next);
        setAutoPadded((prev) => ({ ...prev, day: true }));
        syncIfComplete(next, onChange);
        isAutoAdvancing.current = true;
        yearRef.current?.focus();
        return;
      }
      // Two digits entered; reject invalid days (00, 32+) then advance
      if (digits.length === 2) {
        if (Number(digits) > 31 || Number(digits) === 0) return;
        setSegments(next);
        syncIfComplete(next, onChange);
        isAutoAdvancing.current = true;
        yearRef.current?.focus();
        return;
      }
      setSegments(next);
      syncIfComplete(next, onChange);
    } else {
      const next = { ...segments, year: digits };
      setSegments(next);
      syncIfComplete(next, onChange);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, segment: SegmentKey) => {
    const input = e.currentTarget;
    const prevRef = segment === 'month' ? null : segment === 'day' ? monthRef : dayRef;
    const nextRef = segment === 'month' ? dayRef : segment === 'day' ? yearRef : null;

    if (e.key === 'Backspace' && input.value === '' && prevRef?.current) {
      e.preventDefault();
      prevRef.current.focus();
      return;
    }
    if (e.key === 'ArrowLeft' && input.selectionStart === 0 && prevRef?.current) {
      e.preventDefault();
      prevRef.current.focus();
      return;
    }
    if (e.key === 'ArrowRight' && input.selectionStart === input.value.length && nextRef?.current) {
      e.preventDefault();
      nextRef.current.focus();
      return;
    }
  };

  const handleSegmentFocus = (segment: SegmentKey) => {
    if (isObfuscated(segments[segment]) && !cleared[segment]) {
      setSegments((prev) => ({ ...prev, [segment]: '' }));
      setCleared((prev) => ({ ...prev, [segment]: true }));
    }
  };

  const handleInnerFocus = (segment: SegmentKey, e: React.FocusEvent<HTMLInputElement>) => {
    setFocusedSegment(segment);
    handleSegmentFocus(segment);
    requestAnimationFrame(() => e.target.select());
  };

  const makeHandleSegmentBlur =
    (onChange: (v: string | null) => void) => (segment: SegmentKey, e: React.FocusEvent) => {
      let next = segments;
      if (isAutoAdvancing.current) {
        isAutoAdvancing.current = false;
      } else {
        const val = segments[segment];
        if ((segment === 'month' || segment === 'day') && val.length === 1 && Number(val) > 0) {
          next = { ...segments, [segment]: `0${val}` };
          setSegments(next);
          setAutoPadded((prev) => ({ ...prev, [segment]: true }));
          syncIfComplete(next, onChange);
        }
      }

      const related = e.relatedTarget as Node | null;
      if (related && wrapperRef.current?.contains(related)) {
        return;
      }
      setFocusedSegment(null);
      const composed = compose(next);
      if (composed) binding.onBlur();
    };

  return (
    <Controller
      control={form.control}
      name={key}
      render={({ field, fieldState: { error } }) => (
        <FormControl
          fullWidth
          error={!!error}
        >
          <SegmentWrapper
            ref={wrapperRef}
            role='group'
            aria-label={component.label}
            focused={focusedSegment != null}
            hasError={!!error}
          >
            {SEGMENT_CONFIG.map((seg, i) => {
              const isReadonly = isObfuscated(segments[seg.key]) && !cleared[seg.key];
              const rawValue = segments[seg.key];
              const displayValue = formatForDisplay(rawValue, seg.key, autoPadded[seg.key]);
              const isActive = rawValue !== '' || focusedSegment === seg.key;

              return (
                <Cell
                  key={seg.key}
                  hasDivider={i > 0}
                  onClick={() => refs[seg.key].current?.focus()}
                >
                  <CellLabel active={isActive}>{seg.placeholder}</CellLabel>
                  <CellInput
                    ref={refs[seg.key]}
                    aria-label={seg.ariaLabel}
                    value={displayValue}
                    readOnly={isReadonly}
                    inputMode='numeric'
                    autoComplete='off'
                    maxLength={seg.maxLength}
                    active={isActive}
                    onChange={(e) => makeHandleChange(field.onChange)(seg.key, e.target.value)}
                    onFocus={(e) => handleInnerFocus(seg.key, e)}
                    onBlur={(e) => makeHandleSegmentBlur(field.onChange)(seg.key, e)}
                    onKeyDown={(e) => handleKeyDown(e, seg.key)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Cell>
              );
            })}
          </SegmentWrapper>
          {(error?.message ?? component.description) && (
            <FormHelperText>{error?.message ?? component.description}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

const SegmentWrapper = styled('div')<{ focused: boolean; hasError: boolean }>`
  display: flex;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid;
  border-color: ${({ theme, focused, hasError }) =>
    hasError ? theme.palette.error.main : focused ? theme.palette.tertiary.main : theme.palette.border.main};
  box-shadow: ${({ theme, focused, hasError }) =>
    focused && hasError
      ? `0 0 0 1px ${theme.palette.error.main}`
      : focused
        ? `0 0 0 1px ${theme.palette.tertiary.main}`
        : 'none'};
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
`;

const Cell = styled('div')<{ hasDivider: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  position: relative;
  cursor: text;
  border-left: ${({ theme, hasDivider }) => (hasDivider ? `1px solid ${theme.palette.border.secondary}` : 'none')};
`;

const CellLabel = styled('span')<{ active: boolean }>`
  font-size: ${({ theme, active }) => (active ? theme.typography.overline.fontSize : theme.typography.body1.fontSize)};
  color: ${({ theme }) => theme.palette.text.secondary};
  line-height: 1;
  user-select: none;
  position: absolute;
  top: ${({ active }) => (active ? '0px' : '50%')};
  transform: ${({ active }) => (active ? 'translateY(-50%)' : 'translateY(-50%)')};
  background: ${({ theme, active }) => (active ? theme.palette.background.paper : 'transparent')};
  padding: ${({ active }) => (active ? '0 4px' : '0')};
  transition:
    top 0.2s ease,
    font-size 0.2s ease,
    background 0.2s ease,
    padding 0.2s ease;
  pointer-events: none;
`;

const CellInput = styled('input')<{ active: boolean }>`
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  font-family: inherit;
  font-size: 1rem;
  color: inherit;
  width: 100%;
  padding: 0;
  caret-color: ${({ active }) => (active ? 'auto' : 'transparent')};
`;
